import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams, createSearchParams } from "react-router-dom";
import { Header } from "../../../components/header";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { Select, SearchBar } from "../../../components";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../components/tables";
import { SendIcon } from "../../../components/icons";
import { useArticles } from "../hooks";
import { CustomerSelectSearch } from "../../customers/components";
import { cleanObject } from "../../../helpers/clean-object";
import { ArticleType } from "../../../__generated__/globalTypes";
import { articleTypeLabel } from "../../../helpers/enum-labels";

interface ISearchForm {
  customerId?: string;
  type?: string;
}

export const Articles: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const form = useForm<ISearchForm>({
    mode: "all",
    defaultValues: {
      customerId: searchParams.get("customerId") ?? "",
      type: searchParams.get("type") ?? "",
    },
  });

  const onSubmit = () => {
    const values = form.getValues();
    navigate({
      pathname: "/articles",
      search: `?${createSearchParams(cleanObject(values))}`,
    });
  };

  const customerId = searchParams.get("customerId");
  const type = searchParams.get("type");

  const where: any = {};
  if (customerId) where.customerId = +customerId;
  if (type) where.type = type;

  const { data, loading } = useArticles({ where });

  return (
    <>
      <Header
        title="Articles"
        subtitle="Articles rédigés pour les sites des clients"
        buttons={[
          {
            actionText: "Nouvel article",
            bgColor: "indigo",
            textColor: "white",
            link: `/article/create`,
            icon: <SendIcon />,
          },
        ]}
      />

      <div className="main-container">
        <SearchBar
          form={form}
          onSubmit={onSubmit}
          placeholder=""
          advancedFilters={
            <>
              <CustomerSelectSearch form={form} name="customerId" />
              <Select label="Type" className="w-full" {...form.register("type")}>
                <option value="">Tous</option>
                {Object.keys(ArticleType).map((key) => (
                  <option key={key} value={key}>
                    {articleTypeLabel(key)}
                  </option>
                ))}
              </Select>
            </>
          }
        />

        <div className="p-4 mb-6 bg-white shadow rounded overflow-x-auto">
          {loading ? (
            <Loading />
          ) : !data?.articles?.results?.length ? (
            <EmptyList text="Aucun article" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow hover={false}>
                  <TableCell header>Titre</TableCell>
                  <TableCell header>Type</TableCell>
                  <TableCell header>Site</TableCell>
                  <TableCell header>Client</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.articles?.results?.map((article) => (
                  <TableRow
                    key={`article-${article.id}`}
                    onClick={() => navigate(`/article/${article.id}/update`)}
                  >
                    <TableCell>{article.title}</TableCell>
                    <TableCell>{articleTypeLabel(article.type)}</TableCell>
                    <TableCell>{article.site}</TableCell>
                    <TableCell>{article.customer?.name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </>
  );
};
